import { useFileFetcher } from '@/services/queryUtils';
import { useDerivedNetworkContext } from '@/context';
import { downloadFile } from '@/utils/dataDownloadUtils';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useReleaseFlag } from '@/hooks';
import type { DateRange, ShowFilter } from '@/types';

/**
 * Custom hook to handle CSV export for gender plays data.
 * Calls the server-side export endpoint and triggers a file download.
 * Applies the same audienceSources filter used by GenderModalContent.
 * @returns Object containing handleCSVExport function
 */
const useGenderCsvExport = () => {
  const fetchFile = useFileFetcher();
  const { selectedNetwork } = useDerivedNetworkContext();
  const { audienceSources } = useFlags<{ audienceSources?: string[] }>();
  const sources = audienceSources ?? ['acast', 'phyllo'];
  const flag = useReleaseFlag('audience_page_backfill');

  const handleCSVExport = async (
    dateRange: DateRange | undefined,
    selectedShow: ShowFilter
  ) => {
    if (!dateRange) return;

    const showIdsParam =
      selectedShow && selectedShow !== 'all' ? `&showIds=${selectedShow}` : '';
    const sourcesParam = sources.map((source) => `&sources=${source}`).join('');
    const flagParam = flag ? `&flag=${flag}` : '';
    const path = `/v2/networks/${selectedNetwork.id}/audience/gender-plays/export?from=${dateRange.from}&to=${dateRange.to}${showIdsParam}${sourcesParam}${flagParam}`;
    const { blob, fileName } = await fetchFile(path);
    downloadFile(blob, fileName);
  };

  return { handleCSVExport };
};

export default useGenderCsvExport;
